// Phase 9B — User Intelligence / CRM styles.
//
// Token-only CSS. No hex literals. Premium design system.

import type { CSSProperties } from "react";
import { cardLow, cardMedium } from "../../styles/elevation.ts";
import { transitionStandard, transitionFast } from "../../styles/motion.ts";

// ─── Shell ────────────────────────────────────────────────────

export const intelRoot: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-4)",
};

export const intelHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const intelTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.4rem, 3vw, 2rem)",
  letterSpacing: "3px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const intelSubtitle: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  marginTop: "4px",
};

// ─── Engagement banner ────────────────────────────────────────

export const engagementBanner: CSSProperties = {
  ...cardMedium,
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  gap: "var(--sz-space-4)",
  padding: "var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderTop: "3px solid var(--sz-amber)",
};

export const engagementLevel: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-1)",
  minWidth: "100px",
};

export const levelBadge: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "clamp(1.6rem, 3vw, 2.4rem)",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-amber)",
  lineHeight: 1,
};

export const levelLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const engagementStats: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
  gap: "var(--sz-space-3)",
};

export const engStatCell: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
};

export const engStatValue: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-md)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
  lineHeight: 1,
};

export const engStatLabel: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

// ─── Section ──────────────────────────────────────────────────

export const sectionHeader: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

export const sectionTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.2rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-primary)",
};

export const sectionCount: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  color: "var(--sz-text-muted)",
};

// ─── Intel cards grid ─────────────────────────────────────────

export const intelGrid: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
  gap: "var(--sz-space-2)",
};

export const intelCard: CSSProperties = {
  ...cardLow,
  ...transitionStandard,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-3)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
  borderLeft: "3px solid var(--sz-border-subtle)",
};

export const intelCardKey: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1.5px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const intelCardValue: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-md)",
  fontWeight: 600,
  color: "var(--sz-text-primary)",
};

export const intelCardMeta: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "var(--sz-space-2)",
};

export const confidenceBar: CSSProperties = {
  height: "3px",
  borderRadius: "2px",
  background: "var(--sz-surface-recessed)",
  flex: 1,
  overflow: "hidden",
};

export const confidenceFill: CSSProperties = {
  height: "100%",
  borderRadius: "2px",
  background: "var(--sz-amber)",
  transition: "width 0.3s ease",
};

export const sourceTag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  padding: "2px 6px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
};

// ─── Category pills ───────────────────────────────────────────

const categoryBase: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "2px 8px",
  borderRadius: "var(--sz-radius-sm)",
};

export const CATEGORY_STYLES: Record<string, CSSProperties> = {
  preference: { ...categoryBase, background: "var(--sz-surface-recessed)", color: "var(--sz-text-secondary)" },
  behavior: { ...categoryBase, background: "rgba(59, 130, 246, 0.12)", color: "var(--sz-info-text)" },
  engagement: { ...categoryBase, background: "rgba(245, 158, 11, 0.12)", color: "var(--sz-amber)" },
  risk_profile: { ...categoryBase, background: "rgba(239, 68, 68, 0.12)", color: "var(--sz-error-text)" },
  market_affinity: { ...categoryBase, background: "rgba(34, 197, 94, 0.12)", color: "var(--sz-success-text)" },
  performance: { ...categoryBase, background: "rgba(168, 85, 247, 0.12)", color: "var(--sz-text-secondary)" },
};

// ─── Notes ────────────────────────────────────────────────────

export const notesColumn: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-2)",
};

export const noteCard: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  gap: "var(--sz-space-1)",
  padding: "var(--sz-space-3)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const notePinned: CSSProperties = {
  borderLeft: "3px solid var(--sz-amber)",
};

export const noteContent: CSSProperties = {
  fontFamily: "var(--sz-font-body)",
  fontSize: "var(--sz-body-sm)",
  color: "var(--sz-text-primary)",
  lineHeight: 1.5,
};

export const noteMeta: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};

export const noteAuthor: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "1px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
};

export const noteTime: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  color: "var(--sz-text-muted)",
};

export const noteTag: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "8px",
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  padding: "1px 5px",
  borderRadius: "var(--sz-radius-sm)",
  background: "var(--sz-surface-recessed)",
  color: "var(--sz-text-muted)",
};

// ─── Tabs ─────────────────────────────────────────────────────

export const tabBar: CSSProperties = {
  display: "flex",
  gap: "2px",
  borderBottom: "1px solid var(--sz-border-subtle)",
};

export const tabItem: CSSProperties = {
  ...transitionFast,
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  letterSpacing: "1px",
  textTransform: "uppercase",
  padding: "var(--sz-space-2) var(--sz-space-3)",
  background: "transparent",
  borderTop: "none",
  borderRight: "none",
  borderLeft: "none",
  borderBottom: "2px solid transparent",
  color: "var(--sz-text-muted)",
  cursor: "pointer",
};

export const tabItemActive: CSSProperties = {
  ...tabItem,
  color: "var(--sz-text-primary)",
  borderBottom: "2px solid var(--sz-amber)",
};

// ─── Two-column layout ───────────────────────────────────────

export const twoCol: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr",
  gap: "var(--sz-space-4)",
  alignItems: "start",
};

// ─── Empty state ──────────────────────────────────────────────

export const emptyState: CSSProperties = {
  ...cardLow,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "var(--sz-space-2)",
  padding: "var(--sz-space-6) var(--sz-space-4)",
  borderRadius: "var(--sz-radius-md)",
  background: "var(--sz-surface-card)",
};

export const emptyIcon: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "2rem",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
};

export const emptyTitle: CSSProperties = {
  fontFamily: "var(--sz-font-display)",
  fontSize: "1.1rem",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-secondary)",
};

export const emptyNote: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "var(--sz-mono-xs)",
  color: "var(--sz-text-muted)",
  textAlign: "center",
  maxWidth: "360px",
};

export const phantomBadge: CSSProperties = {
  fontFamily: "var(--sz-font-mono)",
  fontSize: "9px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  color: "var(--sz-text-muted)",
  opacity: 0.5,
  textAlign: "center",
  padding: "var(--sz-space-2) 0",
};
